<script setup lang="ts">
import { useBackgroundVariant } from '@ui/composables/button/useBackgroundVariant'
import { useSize } from '@ui/composables/button/useSize'
import { useTextVariant } from '@ui/composables/button/useTextVariant'
import { GTMEvent } from '@ui/constants/gtm'
import type { BorderRadius, Color, GTMTrackingEvent, Size, Variant } from '@ui/types/button'

// Safely initialize GTM tracking if available
// The GTM module may not be installed in all packages that extend from ui
// (e.g., trakr, dev-tools). This ensures buttons work regardless of GTM availability.
const gtm = (() => {
  try {
    if (typeof useGtm === 'function') {
      return useGtm()
    }
  } catch {
    // GTM module not available in this package
  }
  return null
})()

const props = withDefaults(
  defineProps<{
    block?: boolean
    color?: Color
    dark?: boolean
    icon?: boolean
    rounded?: BorderRadius
    size?: Size
    textColor?: Color
    to?: string
    href?: string
    target?: '_blank' | '_self'
    trackEvent?: GTMTrackingEvent
    type?: 'button' | 'submit' | 'reset'
    variant?: Variant
  }>(),
  {
    block: false,
    color: 'default',
    dark: false,
    icon: false,
    rounded: 'md',
    size: 'md',
    textColor: undefined,
    to: undefined,
    href: undefined,
    target: '_blank',
    trackEvent: undefined,
    type: 'button',
    variant: 'default'
  }
)

const { block, color, dark, icon, rounded, size, textColor, to, href, target, trackEvent, variant } = toRefs(props)

const handleClick = () => {
  if (trackEvent.value) {
    gtm?.trackEvent({
      ...trackEvent.value,
      event: GTMEvent.PortalClick
    })
  }

  if (to.value || href.value) {
    navigateTo(
      to.value || href.value,
      href.value
        ? {
            external: true,
            open: { target: target.value || '_blank' }
          }
        : undefined
    )
  }
}
</script>

<template>
  <button
    :class="[
      'inline-flex cursor-pointer select-none items-center justify-center space-x-1 border whitespace-nowrap disabled:pointer-events-none disabled:opacity-50',
      [
        block && 'w-full',
        dark && 'text-white hover:bg-opacity-10',
        icon && 'rounded-full',
        variant !== 'outlined' && 'border-transparent'
      ],
      useTailwindClasses([
        useBackgroundVariant(variant, color),
        [!icon, useBorderRadius(rounded)],
        [!icon, useSize(size).padding],
        [!icon, useSize(size).text],
        [icon, useSize(size).icon],
        useTextVariant(variant, textColor ?? color)
      ])
    ]"
    :type="type"
    :to="to"
    @click="handleClick"
  >
    <div v-if="$slots.prepend">
      <slot name="prepend" />
    </div>

    <div>
      <slot />
    </div>

    <div v-if="$slots.append">
      <slot name="append" />
    </div>
  </button>
</template>
